import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Users, UserPlus, Target } from 'lucide-react';
import { AllUsersTab, CreateUserTab, ManualScoreTab, UserScoresTab } from '../components/admin';

export default function UserManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [existingScorecards, setExistingScorecards] = useState([]);
  
  // User scores state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userScores, setUserScores] = useState([]);
  const [loadingScores, setLoadingScores] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScorecards = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/scorecards`);
      setExistingScorecards(response.data);
    } catch (error) {
      // Silent fail for scorecards fetch
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchScorecards();
  }, [fetchUsers, fetchScorecards]);

  const handleViewScores = async (userToView) => {
    setSelectedUser(userToView);
    setActiveTab('user-scores');
    setLoadingScores(true);
    
    try {
      const response = await axios.get(`${API}/admin/users/${userToView.id}/scores`);
      setUserScores(Array.isArray(response.data) ? response.data : (response.data?.scores || []));
    } catch (error) {
      toast.error('Failed to load user scores');
    } finally {
      setLoadingScores(false);
    }
  };

  const handleBackFromScores = () => {
    setActiveTab('users');
    setSelectedUser(null);
    setUserScores([]);
  };

  const handleRefreshScores = async () => {
    if (!selectedUser) return;
    setLoadingScores(true);
    try {
      const response = await axios.get(`${API}/admin/users/${selectedUser.id}/scores`);
      setUserScores(Array.isArray(response.data) ? response.data : (response.data?.scores || []));
    } catch (error) {
      toast.error('Failed to refresh scores');
    } finally {
      setLoadingScores(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-600 font-inter">Loading users...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            <h1 className="font-barlow font-bold text-4xl text-slate-900 uppercase tracking-tight">
              User Management
            </h1>
          </div>
          <p className="text-stone-600 font-inter">
            Manage users, permissions, and manual score entries
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-stone-200">
          <button
            onClick={() => {
              setActiveTab('users');
              setSelectedUser(null);
            }}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'users' || activeTab === 'user-scores'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Users
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'create'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Create User
          </button>
          <button
            onClick={() => setActiveTab('manual-score')}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'manual-score'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            Manual Score Entry
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <AllUsersTab 
            users={users} 
            onRefresh={fetchUsers}
            onViewScores={handleViewScores}
          />
        )}

        {activeTab === 'user-scores' && selectedUser && (
          <UserScoresTab 
            selectedUser={selectedUser}
            userScores={userScores}
            loadingScores={loadingScores}
            onBack={handleBackFromScores}
            onRefresh={handleRefreshScores}
          />
        )}

        {activeTab === 'create' && (
          <CreateUserTab onUserCreated={fetchUsers} />
        )}

        {activeTab === 'manual-score' && (
          <ManualScoreTab 
            users={users}
            existingScorecards={existingScorecards}
            onScoreCreated={fetchScorecards}
          />
        )}
      </div>
    </Layout>
  );
}
